import './searchAssistantContent.scss';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ChatWindow} from '../common/chatWindow';
import {
    AiAssistantConversation,
    AiAssistantConversationContext,
    DEFAULT_ASSISTANT_CONVERSATION_CONTEXT,
    Role,
} from '../common/types';
import {
    doAddMessageRequest,
    doDeleteConversationRequest,
    doUpdateConversationContextRequest
} from '../duck/assistant-actions';
import {fetchStreamedAiResponse, getHibuAssistantRequestWithSearchRequest} from '../common/utils';
import {showError} from '../../message-box/message-box-actions';
import {SearchPageType} from '../../search/search-config';
import {useDispatch} from 'react-redux';
import {getCurrentLanguage} from '../../i18n-service';
import {SearchRequest} from '../../search/search-types';
import {ChatInput} from '../common/chatInput';

export type Props = {
    conversation: AiAssistantConversation | undefined;
    conversationId: string;
    activeSearchRequest: SearchRequest;
};

export const SearchAssistantContent = ({
                                           conversation,
                                           conversationId,
                                           activeSearchRequest
                                       }: Props): React.JSX.Element | null => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const conversationContext: AiAssistantConversationContext =
        conversation?.context ?? DEFAULT_ASSISTANT_CONVERSATION_CONTEXT;
    const currentLanguage = getCurrentLanguage();

    const [answer, setAnswer] = useState('');

    const fetchResponseAndUpdateConversation = async (
        userMessage: string,
        contextOverride: AiAssistantConversationContext = conversationContext,
    ) => {
        if (!userMessage?.trim() || !conversation) {
            return;
        }

        dispatch(doUpdateConversationContextRequest(conversationId, contextOverride));
        dispatch(
            doAddMessageRequest(
                conversationId,
                {
                    text: userMessage,
                    role: Role.USER,
                    metadata: {context: contextOverride},
                },
                activeSearchRequest,
            ),
        );

        const request = getHibuAssistantRequestWithSearchRequest(
            activeSearchRequest,
            currentLanguage,
            userMessage,
            conversation,
            contextOverride,
        );

        fetchStreamedAiResponse(request, {
            onToken: (token) => setAnswer((prev) => prev + token),
        })
            .then((hibuAssistantMessage) => {
                dispatch(doAddMessageRequest(conversationId, hibuAssistantMessage, activeSearchRequest));
                setAnswer('');
            })
            .catch((error) => {
                console.error('Error in fetchStreamedAiResponse:', error);
                setAnswer('');
                dispatch(showError(SearchPageType.BASIC, 'assistant.error.responseError'));
            });
    };

    const handleDelete = async () => {
        dispatch(doDeleteConversationRequest(conversationId));
    };

    return (
        <div className={'hibu-assistant'}>
            <ChatWindow
                conversation={conversation}
                currentMessage={{
                    text: answer,
                    role: Role.ASSISTANT,
                }}
                suggestionsEnabled={false}
                updateConversationForSuggestions={fetchResponseAndUpdateConversation}
                renderSources={true}
            />
            <div className={'chat-input-container'}>
                <ChatInput
                    conversationId={conversationId}
                    welcomeMessage={t('assistant.input-search-placeholder-mult')}
                    getAiResponse={fetchResponseAndUpdateConversation}
                    handleResetConversation={handleDelete}
                />
            </div>
        </div>
    );
};
